import React from 'react';

import {
  CourseFilterButton,
  CourseFilterKind,
  KIND_ICON,
  KIND_LABEL,
} from './CourseFilterDrawer';

// Played first: it is the list a member uses most, and the one the flag in the top bar counts.
const CHIP_ORDER: CourseFilterKind[] = ['played', 'bucket', 'home'];

interface ExploreFilterChipsProps {
  /** How many narrowings are on, for the Filters button's badge. */
  activeCount: number;
  isFilterOpen: boolean;
  onFilterClick: () => void;
  /** The member's lists currently switched on. */
  kinds: Set<CourseFilterKind>;
  /** How many courses the member has in each list. All 0 when signed out. */
  counts: Record<CourseFilterKind, number>;
  /**
   * Whether the visitor is signed in. Signed out, the chips still show (so a visitor can see what
   * a member gets), and a tap goes to onSignedOutChipClick instead of filtering.
   */
  isMember: boolean;
  onToggleKind: (kind: CourseFilterKind) => void;
  onSignedOutChipClick: () => void;
}

// The row under the search box: the Filters button at the far left, then one chip per member list
// (the client's call, 2026-11-04, after the reference layout). The chips are the same switches as
// the list section of the filter drawer, so the two always agree: both read and set Explore's one
// `courseFilters` set.
//
// One line that scrolls sideways rather than wrapping. A wrap would push the map down by a second
// row on a narrow phone, and the row is meant to be the same height everywhere.
const ExploreFilterChips: React.FC<ExploreFilterChipsProps> = ({
  activeCount,
  isFilterOpen,
  onFilterClick,
  kinds,
  counts,
  isMember,
  onToggleKind,
  onSignedOutChipClick,
}) => (
  // -mx/px gives the scroll room at each end so a chip's shadow is not clipped at the edge.
  // The native scroll bar is hidden: on a desktop it drew a grey bar across the map.
  <div className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
    <CourseFilterButton
      activeCount={activeCount}
      isOpen={isFilterOpen}
      onClick={onFilterClick}
    />

    {CHIP_ORDER.map((kind) => {
      const Icon = KIND_ICON[kind];
      const isOn = kinds.has(kind);
      // Signed in with an empty list: shown but disabled, the same rule as the drawer (R28).
      // An empty list's chip would only empty the map.
      const isEmpty = isMember && counts[kind] === 0;

      return (
        <button
          key={kind}
          type="button"
          disabled={isEmpty}
          aria-pressed={isMember ? isOn : undefined}
          onClick={() =>
            isMember ? onToggleKind(kind) : onSignedOutChipClick()
          }
          className={`flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium shadow-lg backdrop-blur transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40 ${
            isOn
              ? // BRAND.md: text on a gold fill is BLACK.
                'border-primary bg-primary text-secondary'
              : 'border-primary/30 bg-charcoal/90 text-white hover:border-primary'
          }`}
        >
          <Icon
            className={`h-4 w-4 ${isOn ? 'text-secondary' : 'text-primary'}`}
          />
          <span className="whitespace-nowrap">{KIND_LABEL[kind]}</span>
        </button>
      );
    })}
  </div>
);

export default ExploreFilterChips;
